import React, { useRef, useState } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Cropper } from '@/components/ui/image-crop';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import { Upload, RefreshCw, Trash2 } from 'lucide-react';

interface ImageCropFieldProps {
  /** Uploaded image URL ('' when empty). */
  value: string;
  onChange: (url: string) => void;
  /**
   * Crop aspect ratio (width / height). Default 1.
   * Pass `null` for a free (native) ratio — the full image is preselected and
   * can be uploaded as-is at any ratio, or trimmed freely.
   */
  aspectRatio?: number | null;
  /** Round crop selection (avatars). */
  circularCrop?: boolean;
  maxSizeMB?: number;
  /** Help line rendered under the field. */
  hint?: string;
}

const CHECKERBOARD: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%, #e5e7eb)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 8px 8px',
  backgroundRepeat: 'repeat',
};

/**
 * ImageCropField — gallery-style upload + crop field for forms.
 *
 * Mirrors the "Add Image" modal in Image Gallery Management: dashed drop
 * zone → inline `Cropper` (ui/image-crop) → checkerboard preview with
 * Change / Remove actions. The cropped result is uploaded to
 * `/api/images/upload` and the resulting URL is passed to `onChange`.
 */
const ImageCropField: React.FC<ImageCropFieldProps> = ({
  value,
  onChange,
  aspectRatio = 1,
  circularCrop = false,
  maxSizeMB = 2,
  hint,
}) => {
  const [step, setStep] = useState<'idle' | 'cropping'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const cropperRef = useRef<{ applyCrop: () => Promise<void> }>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetUploadState = () => {
    setStep('idle');
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openFileUpload = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Only .png and .jpeg files are allowed.');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`File size must be less than ${maxSizeMB}MB.`);
      return;
    }

    setError('');
    setSelectedFile(file);
    setStep('cropping');
  };

  const handleCropComplete = async (croppedImage: string) => {
    if (!croppedImage) return;

    setUploading(true);
    try {
      const response = await fetch(croppedImage);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append('file', blob, 'cropped-image.png');

      const res = await axios.post(BASE_URL + '/api/images/upload', formData);
      onChange(res.data.url);
      resetUploadState();
    } catch {
      setError('Image upload failed.');
      toast.error('Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const openCropperForExisting = () => {
    if (!value) return;
    fetch(value)
      .then((res) => res.blob())
      .then((blob) => {
        setSelectedFile(new File([blob], 'image.png', { type: blob.type }));
        setStep('cropping');
      })
      .catch(() => toast.error('Failed to load image for editing'));
  };

  return (
    <div className="space-y-2">
      {step === 'idle' ? (
        !value ? (
          <Card
            className="group flex max-h-[200px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
            onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
              e.preventDefault();
              e.currentTarget.classList.add('bg-muted/50');
            }}
            onDragLeave={(e: React.DragEvent<HTMLDivElement>) => {
              e.currentTarget.classList.remove('bg-muted/50');
            }}
            onDrop={(e: React.DragEvent<HTMLDivElement>) => {
              e.preventDefault();
              e.currentTarget.classList.remove('bg-muted/50');
              const files = e.dataTransfer.files;
              if (files.length > 0) {
                handleFileChange({ target: { files } } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
            onClick={openFileUpload}
          >
            <div className="grid space-y-3">
              <div className="flex items-center gap-x-2 text-muted-foreground justify-center">
                <Upload className="size-5" />
                <div>
                  Drop image here or{' '}
                  <Button
                    type="button"
                    variant="link"
                    className="text-primary p-0 h-auto font-normal"
                    onClick={(e) => {
                      e.stopPropagation();
                      openFileUpload();
                    }}
                  >
                    browse
                  </Button>{' '}
                  to upload
                </div>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".png,.jpg,.jpeg"
              onChange={handleFileChange}
            />
            <span className="text-xs text-muted-foreground mt-1 block">
              Supported: JPG, PNG (max {maxSizeMB} MB)
            </span>
          </Card>
        ) : (
          <div className="space-y-2">
            <Card
              className="group flex max-h-[400px] w-full flex-col items-center justify-center gap-4 py-8 border-dashed text-sm cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={openCropperForExisting}
            >
              <div
                className="relative flex items-center justify-center max-h-64 w-full sm:max-w-64 rounded-2xl overflow-hidden"
                style={CHECKERBOARD}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={value}
                  alt="uploaded"
                  className="max-h-64 max-w-full w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
                  title="Click to edit image"
                />
              </div>
              <div className="flex items-center gap-x-2 text-muted-foreground text-xs">
                <Upload className="size-4" />
                <span>Click to edit or re-upload</span>
              </div>
            </Card>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-foreground"
                onClick={openFileUpload}
              >
                <RefreshCw className="size-3.5" />
                Change
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-danger"
                onClick={() => onChange('')}
              >
                <Trash2 className="size-3.5" />
                Remove
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        )
      ) : (
        <div className="space-y-2">
          <Card className="flex flex-col items-center">
            <div className="w-full border-b p-3">
              <p className="text-sm font-medium text-foreground text-center">
                {aspectRatio === null
                  ? 'Full image selected — drag edges to trim, or upload as-is at its original ratio'
                  : 'Adjust crop area - Drag to move, edges to resize'}
              </p>
            </div>
            <div className="w-full flex justify-center py-3">
              {selectedFile && (
                <Cropper
                  file={selectedFile}
                  aspect={aspectRatio ?? undefined}
                  circularCrop={circularCrop}
                  maxImageSize={maxSizeMB * 1024 * 1024}
                  keepSelection
                  onCrop={handleCropComplete}
                  onCropRef={cropperRef}
                />
              )}
            </div>
          </Card>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => cropperRef.current?.applyCrop()}
              disabled={uploading}
              size="sm"
              loading={uploading}
              loadingText="Uploading..."
            >
              {value ? 'Update' : 'Upload'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={resetUploadState}
              disabled={uploading}
              size="sm"
            >
              Cancel
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".png,.jpg,.jpeg"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      )}
      {error && <div className="text-danger text-xs">{error}</div>}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
};

export default ImageCropField;
